import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import './ConsultantCard.css';
import type { AppointmentTaker } from '../../marketplace/types';
import {
  formatConsultantFee,
  formatConsultantHoursLabel,
  getConsultantAvailabilityHint,
  getConsultantGradient,
  getConsultantInitials,
  getConsultantProfile,
  getConsultantShortName,
  isConsultantBookable,
} from '../utils/consultantDisplay';
import { getConsultantMock } from '../data/consultantMockData';
import { IllustrativeDisclaimer } from '../../../components/IllustrativeDisclaimer';

interface ConsultantCardProps {
  taker: AppointmentTaker;
  index: number;
}

// Badge tokens — "Free" thumbnail badge stays light green
const BADGE_STYLES = {
  featured: { bg: '#fff1f1', color: '#FF0100' },
  free:     { bg: '#ECFDF5', color: '#059669' },
  new:      { bg: '#EFF6FF', color: '#1D4ED8' },
} as const;

export const ConsultantCard: React.FC<ConsultantCardProps> = ({ taker, index }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const slug       = taker.slug ?? taker.userId;
  const mock       = getConsultantMock(slug, taker.displayName);
  const profile    = getConsultantProfile(slug);
  const bookable   = isConsultantBookable(taker);
  const shortName  = getConsultantShortName(taker.displayName);
  const initials   = getConsultantInitials(shortName);
  const gradient   = getConsultantGradient(slug, !bookable);
  const fee        = formatConsultantFee(taker.fee, taker.currency || 'CAD');
  const hoursLabel = formatConsultantHoursLabel(taker);
  const availHint  = getConsultantAvailabilityHint(taker);
  const isAvail    = availHint.toLowerCase().includes('today');

  // Badge
  const badgeKey = mock?.badge ?? (profile.featured ? 'featured' : null);
  const badge    = badgeKey ? BADGE_STYLES[badgeKey] : null;

  // Subtitle: "10+ yrs · Walk-in & Call"
  const modeText = [
    taker.allowWalkIn !== false && 'Walk-in',
    taker.allowCall   !== false && 'Call',
  ].filter(Boolean).join(' & ');
  const subtitle = [mock?.experience, modeText].filter(Boolean).join(' · ');

  const handleBook = () => {
    setOpen(false);
    navigate(`/appointments/${slug}/book`);
  };

  return (
    <>
      {/* ── CARD ─────────────────────────────────────────────── */}
      <motion.div
        className="ccard"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: index * 0.09, ease: [0.4, 0, 0.2, 1] }}
        whileHover={{ y: -5, transition: { duration: 0.2 } }}
        onClick={() => setOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setOpen(true)}
        aria-label={`View profile of ${shortName}`}
        style={{ opacity: bookable ? 1 : 0.6 }}
      >
        {/* Thumbnail */}
        <div className="ccard-thumb" style={{ background: mock?.photo ? undefined : gradient }}>
          {mock?.photo ? (
            <img src={mock.photo} alt={shortName} className="ccard-img" />
          ) : (
            <div style={{
              width: '100%', height: '100%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: gradient,
            }}>
              <span style={{ fontSize: '2.4rem', fontWeight: 800, color: '#fff', letterSpacing: '0.06em' }}>
                {initials}
              </span>
            </div>
          )}

          {/* Badge (Featured / New / Free) */}
          {badge && badgeKey && (
            <span className="ccard-badge" style={{ background: badge.bg, color: badge.color }}>
              {badgeKey === 'featured' ? '⭐ Featured' : badgeKey === 'new' ? 'New' : 'Free'}
            </span>
          )}

          {/* Quick view hover pill */}
          <div className="ccard-thumb-overlay">
            <span className="ccard-thumb-hint">
              <i className="ti ti-eye" style={{ fontSize: 13 }} /> Quick view
            </span>
          </div>
        </div>

        {/* Body — name, subtitle, bio, availability */}
        <div className="ccard-body">
          <h3 className="ccard-name">{taker.displayName}</h3>
          {subtitle && <p className="ccard-subtitle">{subtitle}</p>}
          {mock?.bio && <p className="ccard-info">{mock.bio}</p>}
          <p className="ccard-avail-line">
            <i className={`ti ${isAvail ? 'ti-circle-check' : 'ti-clock'}`} />
            {availHint}
          </p>
        </div>

        {/* Specialty tags — max 2, compact */}
        {profile.specialties && profile.specialties.length > 0 && (
          <div className="ccard-tags">
            {profile.specialties.slice(0, 2).map((s) => (
              <span key={s} className="ccard-tag">{s}</span>
            ))}
          </div>
        )}

        {/* Footer — fee + "View profile →" */}
        <div className="ccard-foot">
          <span className={`ccard-fee ${fee.isFree ? 'free' : ''}`}>
            {fee.amount}
            {fee.suffix && !fee.isFree && (
              <span style={{ fontSize: 11, fontWeight: 400, color: '#9ca3af', marginLeft: 4 }}>
                {fee.suffix}
              </span>
            )}
          </span>
          <span className="ccard-cta">
            View profile <i className="ti ti-arrow-right" style={{ fontSize: 11 }} />
          </span>
        </div>
      </motion.div>

      {/* ── MODAL ────────────────────────────────────────────── */}
      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop */}
            <motion.div
              className="ccard-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setOpen(false)}
            />

            {/* Dialog */}
            <div className="ccard-modal-wrapper">
              <motion.div
                className="ccard-modal"
                role="dialog"
                aria-modal="true"
                aria-label={`Profile of ${shortName}`}
                initial={{ opacity: 0, scale: 0.94, y: 18 }}
                animate={{ opacity: 1, scale: 1,    y: 0  }}
                exit={{   opacity: 0, scale: 0.94, y: 18  }}
                transition={{ duration: 0.26, ease: [0.4, 0, 0.2, 1] }}
              >
                {/* Close */}
                <button className="ccard-modal-close" onClick={() => setOpen(false)} aria-label="Close">
                  <i className="ti ti-x" />
                </button>

                {/* Header photo */}
                <div
                  className="ccard-modal-header"
                  style={{ background: mock?.photo ? undefined : gradient, position: 'relative' }}
                >
                  {mock?.photo
                    ? <img src={mock.photo} alt={shortName} className="ccard-modal-header-img" />
                    : (
                      <div style={{
                        width: '100%', height: '100%', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', background: gradient,
                      }}>
                        <span style={{ fontSize: '3.2rem', fontWeight: 800, color: '#fff' }}>{initials}</span>
                      </div>
                    )
                  }
                  <IllustrativeDisclaimer className="illus-disclaimer--modal" />

                  {/* Name + title */}
                  <div className="ccard-modal-header-info">
                    <div>
                      <motion.h2
                        className="ccard-modal-name"
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1, duration: 0.32 }}
                      >
                        {taker.displayName}
                      </motion.h2>
                      {mock?.title && (
                        <motion.p
                          className="ccard-modal-job"
                          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.16, duration: 0.32 }}
                        >
                          {mock.title}
                        </motion.p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Body */}
                <div className="ccard-modal-body">

                  {/* Bio */}
                  {mock?.bio && (
                    <motion.p
                      className="ccard-modal-bio"
                      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.18, duration: 0.35 }}
                    >
                      {mock.bio}
                    </motion.p>
                  )}

                  {/* Chips — specialties first (orange), then languages (blue) */}
                  {(profile.specialties?.length || profile.languages?.length) && (
                    <motion.div
                      className="ccard-modal-chips"
                      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.23, duration: 0.35 }}
                    >
                      {profile.specialties?.map((s) => (
                        <span key={s} className="ccard-modal-chip specialty">{s}</span>
                      ))}
                      {profile.languages?.map((l) => (
                        <span key={l} className="ccard-modal-chip lang">{l}</span>
                      ))}
                    </motion.div>
                  )}

                  {/* Info rows */}
                  <motion.div
                    className="ccard-modal-rows"
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.28, duration: 0.35 }}
                  >
                    {mock?.experience && (
                      <div className="ccard-modal-row">
                        <span className="ccard-modal-row-label">Experience</span>
                        <span className="ccard-modal-row-value">{mock.experience}</span>
                      </div>
                    )}
                    {mock?.sessionDuration && (
                      <div className="ccard-modal-row">
                        <span className="ccard-modal-row-label">Session duration</span>
                        <span className="ccard-modal-row-value">{mock.sessionDuration}</span>
                      </div>
                    )}
                    <div className="ccard-modal-row">
                      <span className="ccard-modal-row-label">Availability</span>
                      <span className="ccard-modal-row-value">{hoursLabel}</span>
                    </div>
                    {taker.allowWalkIn !== false && taker.officeAddress && (
                      <div className="ccard-modal-row">
                        <span className="ccard-modal-row-label">Office</span>
                        <span className="ccard-modal-row-value">
                          {taker.officeAddress.name || taker.officeAddress.fullAddress}
                        </span>
                      </div>
                    )}
                    <div className="ccard-modal-row">
                      <span className="ccard-modal-row-label">Modes</span>
                      <span className="ccard-modal-row-value">
                        {[
                          taker.allowWalkIn !== false && 'Walk-in',
                          taker.allowCall   !== false && 'Phone call',
                        ].filter(Boolean).join(' · ') || 'Not available'}
                      </span>
                    </div>
                  </motion.div>

                  {/* Footer — fee + Book button */}
                  <motion.div
                    className="ccard-modal-foot"
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.34, duration: 0.35 }}
                  >
                    <div>
                      <p className="ccard-modal-fee-label">Consultation fee</p>
                      <p className={`ccard-modal-fee-amount ${fee.isFree ? 'free' : ''}`}>
                        {fee.amount}
                        {fee.suffix && !fee.isFree && (
                          <span style={{ fontSize: 11, fontWeight: 500, color: '#9ca3af', marginLeft: 6 }}>
                            {fee.suffix}
                          </span>
                        )}
                      </p>
                    </div>

                    <button
                      className="ccard-modal-btn"
                      onClick={handleBook}
                      disabled={!bookable}
                    >
                      {bookable
                        ? <><span>Book appointment</span> <i className="ti ti-arrow-right" style={{ fontSize: 13 }} /></>
                        : 'Not available'
                      }
                    </button>
                  </motion.div>

                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default ConsultantCard;
